import React, { CSSProperties } from "react";

import styled from "styled-components";

import { colorCSS, fontSizes, radius, shadow } from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

// Keyframe animation as CSS string
const placeholderGlowKeyframes = `
@keyframes placeholderGlow {
    50% {
        ${redundantCssFromKebab("opacity", "0.37")}
    }
}
`;

const placeholderStyle: CSSProperties = {
    animationDuration: "2s",
    animationIterationCount: "infinite",
    animationName: "placeholderGlow",
    backgroundColor: "currentcolor",
    opacity: 0.5
};

const placeholderImageStyle: CSSProperties = {
    ...placeholderStyle,
    height: 0,
    paddingBottom: "50%"
};

const placeholderTextStyle: CSSProperties = {
    ...placeholderStyle,
    display: "inline-block"
};

const blogArticleStyle: CSSProperties = {
    backgroundColor: colorCSS("N0"),
    borderRadius: radius.button,
    color: `${colorCSS("N800")}`,
    display: "block",
    margin: "2rem 0 1.5rem",
    paddingBlockEnd: "1rem",
    textAlign: "left",
    textDecoration: "none",
    width: "100%"
};

const linkStyle: CSSProperties = {
    ...blogArticleStyle,
    transition: "box-shadow 0.3s"
};

const eyebrowStyle: CSSProperties = {
    color: colorCSS("N400"),
    fontSize: fontSizes.xxs,
    margin: "1rem 1rem 0",
    textTransform: "uppercase"
};

const eyebrowPlaceholderStyle: CSSProperties = {
    ...placeholderTextStyle,
    minHeight: "1rem",
    width: "80%"
};

const titleStyle: CSSProperties = {
    fontSize: fontSizes.sm,
    margin: "0.5rem 1rem 0"
};

const titlePlaceholderStyle: CSSProperties = {
    ...placeholderTextStyle,
    height: "3rem",
    width: "90%"
};

// Styled-components for SSR CSS generation
const PlaceholderTextStyled = styled.span({
    "animationDuration": "2s",
    "animation-duration": "2s",
    "animationIterationCount": "infinite",
    "animation-iteration-count": "infinite",
    "animationName": "placeholderGlow",
    "animation-name": "placeholderGlow",
    "backgroundColor": "currentcolor",
    "background-color": "currentcolor",
    "opacity": 0.5,
    "display": "inline-block"
});

const FallbackBannerStyled = styled.div({
    margin: "-1rem"
});

const PlaceholderImageStyled = styled.div<{
    "borderRadius": string;
    "border-radius": string;
}>({
    animationDuration: "2s",
    animationIterationCount: "infinite",
    animationName: "placeholderGlow",
    backgroundColor: "currentcolor",
    opacity: 0.5,
    height: 0,
    paddingBottom: "50%",
    borderRadius: (props) => props.borderRadius
});

// Inject keyframes into document
if (
    typeof document !== "undefined" &&
    !document.getElementById("placeholder-glow-keyframes")
) {
    const style = document.createElement("style");
    style.id = "placeholder-glow-keyframes";
    style.textContent = placeholderGlowKeyframes;
    document.head.appendChild(style);
}

export const PlaceholderImage: React.FC<
    React.HTMLAttributes<HTMLDivElement>
> = (props) => {
    return (
        <PlaceholderImageStyled
            borderRadius={`${radius.button} ${radius.button} 0 0`}
            {...props}
        />
    );
};

export const PlaceholderText: React.FC<
    React.HTMLAttributes<HTMLSpanElement>
> = (props) => <PlaceholderTextStyled {...props} />;

export const Link: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = (
    props
) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const style: CSSProperties = {
        ...linkStyle,
        ...(isHovered && { boxShadow: shadow.normal }),
        ...props.style
    };
    return (
        // eslint-disable-next-line @atlassian/a11y/anchor-has-content
        <a
            {...props}
            style={style}
            // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
            onMouseEnter={(e) => {
                setIsHovered(true);
                props.onMouseEnter?.(e);
            }}
            // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
            onMouseLeave={(e) => {
                setIsHovered(false);
                props.onMouseLeave?.(e);
            }}
        />
    );
};

export const PlaceholderFrame: React.FC<
    React.HTMLAttributes<HTMLDivElement>
> = (props) => {
    const style: CSSProperties = {
        ...blogArticleStyle,
        cursor: "wait",
        ...props.style
    };
    return <div {...props} style={style} />;
};

export const Eyebrow: React.FC<
    React.HTMLAttributes<HTMLParagraphElement> & { isPlaceholder?: boolean }
> = ({ isPlaceholder, ...props }) => {
    const style: CSSProperties = {
        ...eyebrowStyle,
        ...props.style
    };
    return (
        <p {...props} style={style}>
            {isPlaceholder ? (
                <PlaceholderText style={eyebrowPlaceholderStyle} />
            ) : (
                props.children
            )}
        </p>
    );
};

export const Title: React.FC<
    React.HTMLAttributes<HTMLParagraphElement> & { isPlaceholder?: boolean }
> = ({ isPlaceholder, ...props }) => {
    const style: CSSProperties = {
        ...titleStyle,
        ...props.style
    };
    return (
        <p {...props} style={style}>
            {isPlaceholder ? (
                <PlaceholderText style={titlePlaceholderStyle} />
            ) : (
                props.children
            )}
        </p>
    );
};

//neutralizes 1rem padding in child UiNavBanner
export const FallbackBanner: React.FC<React.HTMLAttributes<HTMLDivElement>> = (
    props
) => <FallbackBannerStyled {...props} />;
